<template>
    <div class="wrapper" v-if="useFacialRecognition">
        <modal-dialog mask-class="bg-blank face-recognition-modal" :hide-close="true" v-if="facialRecognitionFail">
            <h3 class="modal-title text-center" id="modal-title">
                Reconhecimento de Face
            </h3>

            <div class="area">
                <p class="recognition-info">
                    <strong>
                        O reconhecimento da sua face falhou. Se atente à posição em frente à camera, a luminosidade do
                        local onde você está e a itens que podem prejudicar a acurácia.
                    </strong>
                </p>
            </div>
            <div class="modal-footer d-flex-row flex-justify-center">
                <button class="btn btn-primary" @click="candidateRetryFacialRecognition()">Tentar novamente</button>
            </div>
        </modal-dialog>
        <div class="m-t-xl">
            <video ref="webCamera" autoplay muted playsinline style="width: 100%; height: auto" v-show="!isLoading" title="Vídeo da sua câmera" id="cameraFrame"></video>

            <video ref="webCameraHidden" autoplay muted playsinline id="cameraFrameHidden" class="recognition-hidden-video"
                   style=""></video>
        </div>
        <div class="non-blocking-notification" v-if="facialRecognitionFailNonBlocking">
            <span id="close-button" class="close-button" @click="facialRecognitionFailNonBlocking=false">X</span>
            <p>
                O reconhecimento facial falhou. Por favor, verifique a posição em frente à câmera, a luminosidade do ambiente e outros fatores que podem afetar a precisão.
            </p>
        </div>
        <div class="non-blocking-notification" v-if="showKinesisAuthenticationError">
            <span id="close-button" class="close-button" @click="showKinesisAuthenticationError=false">X</span>
            <p>
                Erro na conexão com a stream de vídeo. Verifique sua conexão à internet e as permissões do seu dispositivo. Se o problema persistir, entre em contato com o seu monitor.
            </p>
        </div>
        <face-recognition-modal
                v-if="showModalFaceRecognition"
                :is-first-recognition="false"
                :onRecognitionSuccess="passFacialRecognition"
        ></face-recognition-modal>
    </div>
</template>

<script>
    import axios from 'axios';
    import {v4 as uuidv4} from 'uuid';
    import {getBaseSgpUrlNoSlug} from "../../../scripts/apiService/ApiService";
    import {getCookie, getBase64} from "../../../scripts/utils";
    import ModalDialog from "../../base/Modal";
    import * as RecordRTC from 'recordrtc';
    import {registerInfraction} from "../../../scripts/apiService/proctoring";
    import {Kinesis, StreamProducer} from '../../../scripts/producer'
    import {screenSharingStatus$, screenStreamSubject$} from "../../../scripts/observableStates";
    import FaceRecognitionModal from "./FaceRecognitionDirectiveModal";
    import { setTimeout, clearTimeout } from 'worker-timers';

    export default {
        name: "FaceRecognitionDirective",
        props: ['useFacialRecognition', 'testFinished'],
        data() {
            let variables = this.$store.state.onlineTest.configs.variables;
            let urls = this.$store.state.onlineTest.configs.urls;
            return {
                isLoading: true,
                img: null,
                numberOfRecognitionTries: 0,
                itemGeneratedId: null,
                facialRecognitionFail: false,
                facialRecognitionFailNonBlocking: false,
                showModalFaceRecognition: false,
                faceRecognitionInterval: undefined,
                candidatePhoto: variables.candidate_photo,
                enableTakePhoto: variables.enable_take_photo,
                realTimePhotoValidation: variables.candidate_photo,
                uploadUrl: urls.facial_recognition_upload,
                urlSavePhoto: urls.facial_recognition_save_photo,
                bucketName: variables.bucket_name_s3,
                urlFacialRecognition: urls.facial_recognition,
                token: variables.token_facial_recognition_upload,
                nativeProctoring: variables.native_proctoring,
                peerCheckInterval: null,
                peerConnection: null,
                kinesisStream: true,
                screenStreamSubscription: null,
                failedFaceRecognitionNotification: variables.failed_face_recognition_notification,
                showKinesisAuthenticationError: false,
                retryKinesisInterval: null,
                isMounted: false,
                kinesisConnection: {
                  camera: null,
                  screen: null
                },
                refreshTimers: {
                  camera: null,
                  screen: null
                }
            }
        },
        components: {
            FaceRecognitionModal,
            ModalDialog
        },
        methods: {
            registerStreamingInfraction(){
                let data = {
                    "infraction_type": "STREAM_UNAVAILABLE",
                    "item_generated": this.itemGeneratedId
                };
                registerInfraction(data);
            },
            async initKinesisConnection(source, sourceMedia){
                if (!this.isMounted) return;
                let clientSlug = this.$store.state.onlineTest.configs.variables.client_slug;
                let credentials = await this.fetchCredentials();

                if(credentials) {
                  this.showKinesisAuthenticationError = false;

                  let kinesis = new Kinesis({
                    channelName: `${clientSlug}-${this.itemGeneratedId}-${source}-candidate`,
                    streamName: `${clientSlug}-${this.itemGeneratedId}-${source}-candidate-stream`,
                    accessKeyId: credentials.AccessKeyId,
                    secretAccessKey: credentials.SecretAccessKey,
                    sessionToken: credentials.SessionToken,
                    testFinished: () => this.testFinished
                  });
                  let channelARN = await kinesis.createChannel();

                  this.kinesisConnection[source] = new StreamProducer(channelARN, kinesis, sourceMedia, {
                    onError: () => { this.retryKinesisConnection(source, sourceMedia) },
                    testFinished: () => this.testFinished
                  });
                  await this.kinesisConnection[source].start(null, null);
                  this.scheduleCredentialRefresh(source, sourceMedia, credentials?.Expiration);
                }
                else{
                  this.registerStreamingInfraction();
                  this.showKinesisAuthenticationError = true;
                  this.retryKinesisConnection(source, sourceMedia);
                }
            },
            async fetchCredentials() {
                let url = process.env.VUE_APP_MONITORING_SERVICES_URL + "kinesis/credentials";
                let data = {params: {'test_id': this.itemGeneratedId}, headers: {"Authorization": getCookie("refreshToken")}};
                let credentials = null;
                await axios.get(url, data).then(response => {
                    credentials = JSON.parse(response.data);
                    if (typeof credentials === 'object' && Object.prototype.hasOwnProperty.call(credentials, "error")) credentials = null;
                }).catch(error => {
                    console.error(error)
                });
                return credentials
            },
            scheduleCredentialRefresh(source, sourceMedia, expirationEpoch) {
              if(!expirationEpoch) return;

              const refreshTime = expirationEpoch * 1000 - Date.now() - 2 * 60 * 1000;
              if (refreshTime <= 0) return;

              if(this.refreshTimers[source]){
                clearTimeout(this.refreshTimers[source]);
                this.refreshTimers[source] = null;
              }

              this.refreshTimers[source] = setTimeout(
                () => this.rotateKinesisCredentials(source, sourceMedia),
                refreshTime
              );
            },
            async rotateKinesisCredentials(source, sourceMedia) {
                if(this.testFinished) return;

                const newCreds = await this.fetchCredentials();
                if (!newCreds) return;

                const streamProducer = this.kinesisConnection[source];
                if (!streamProducer) return;

                const { channelName, streamName } = streamProducer.kinesis.options;
                const freshKinesis = new Kinesis({
                    channelName: channelName,
                    streamName: streamName,
                    accessKeyId: newCreds.AccessKeyId,
                    secretAccessKey: newCreds.SecretAccessKey,
                    sessionToken: newCreds.SessionToken,
                    testFinished: () => this.testFinished
                });
                await freshKinesis.createChannel();
                await streamProducer.refreshCredentials(freshKinesis);

                this.scheduleCredentialRefresh(source, sourceMedia, newCreds.Expiration);
            },
            retryKinesisConnection(source, sourceMedia){
                if(!this.isMounted) return;
                if(this.retryKinesisInterval) clearTimeout(this.retryKinesisInterval);
                this.retryKinesisInterval = setTimeout(() => {
                  this.initKinesisConnection(source, sourceMedia);
                }, 10000);
            },
            initRecording(camera, source){
                let url = process.env.VUE_APP_MONITORING_SERVICES_URL + "upload_clip/";
                let itemGeneratedId = this.itemGeneratedId;
                let counter = 0;
                let sessionUuid = uuidv4();
                let clientSlug = this.$store.state.onlineTest.configs.variables.client_slug;

                let recorder = RecordRTC(camera, {
                    recorderType: RecordRTC.MediaStreamRecorder,
                    mimeType: 'video/webm;codecs=vp8',
                    timeSlice: 5000,
                    ondataavailable: function(blob) {
                        let file = new File([blob], 'teste', {
                            type: 'video/webm;codecs=vp8'
                        });

                        let data = {name: source + " - " + counter,
                                    client_slug: clientSlug,
                                    item_generated_id: itemGeneratedId,
                                    session_uuid: sessionUuid};
                        getBase64(file, function(result){
                            data.clip = result;
                            axios.post(url, data, {
                                headers: {"Content-Type": "application/json", "Authorization": getCookie("refreshToken")}
                            });
                        });
                        counter++;
                    }
                });
                recorder.startRecording();
                recorder.camera = camera;
            },
            startCameraShare() {
                let video = document.getElementById("cameraFrame");
                let snapShotVideo = document.getElementById("cameraFrameHidden");
                let component = this;
                navigator.mediaDevices.getUserMedia({audio: false, video: {facingMode: 'user'}})
                .then((stream) => {
                    video.srcObject = stream;
                    snapShotVideo.srcObject = stream;
                    window.stream = stream;
                    snapShotVideo.onloadedmetadata = component.callFacialRecognition;
                    if (component.nativeProctoring) {
                        component.initRecording(stream, "Camera");
                        component.initKinesisConnection("camera", stream);
                    }

                    stream.getTracks().forEach((track) => {
                        track.addEventListener('ended', () => {
                          track.stop();
                          this.startCameraShare();
                        });
                      });
                })
                .catch(function () {
                    alert("Habilite sua câmera e recarregue a página.");
                    location.reload();
                });
            },
            loadCamera() {
                let video = document.getElementById("cameraFrame");
                let snapShotVideo = document.getElementById("cameraFrameHidden");
                let component = this;
                if (window.stream) {
                    window.stream.getTracks()[0].stop();
                }
                if (video) {
                    if (navigator.mediaDevices.getUserMedia) {
                        const resolution =  {width: {ideal: 320}, height: {ideal: 240}, facingMode: "user"};
                        const constraints = {
                            video: resolution,
                            audio: false
                        };
                        navigator.mediaDevices.getUserMedia(constraints)
                            .then((stream) => {
                                video.srcObject = stream;
                                snapShotVideo.srcObject = stream;
                                window.stream = stream;
                                component.isLoading = false;
                                snapShotVideo.onloadedmetadata = component.callFacialRecognition;
                                if (this.nativeProctoring) {
                                    this.initRecording(stream, "Camera");
                                    this.initKinesisConnection("camera", stream);
                                }

                                stream.getTracks().forEach((track) => {
                                    track.addEventListener('ended', () => {
                                      track.stop();
                                      this.startCameraShare();
                                    });
                                  });

                            })
                            .catch(function () {
                                alert("Habilite sua câmera e recarregue a página.");
                                location.reload();
                            });
                    }
                }

                this.initScreenRecording();

            },
            initScreenRecording() {
                if (this.nativeProctoring && navigator.mediaDevices.getDisplayMedia) {
                    this.screenStreamSubscription = screenStreamSubject$.get().subscribe(stream => {
                        if (stream && stream.active) {
                            this.initRecording(stream, "Screen");
                            this.initKinesisConnection("screen", stream);
                        }
                    });
                }
            },
            tryFacialRecognition() {
                let currentCandidate = getCookie('currentCandidate');
                let baseUrl = getBaseSgpUrlNoSlug();
                let url = baseUrl + this.urlFacialRecognition;
                let data = {
                    candidate_key: currentCandidate,
                    data_url_image: this.img,
                    item_generated_id: this.itemGeneratedId,
                    is_first_recognition: this.isFirstRecognition,
                    is_skipped: false
                };
                const headers = {"X-CSRFTOKEN": getCookie('csrftoken'), "Authorization": 'Bearer ' + this.token};

                let component = this;

                axios.post(url, data, {headers: headers, withCredentials: true}).then(function (response) {
                    if (response.data.candidate_has_been_recognized) {
                        component.facialRecognitionFail = false;
                    } else {
                        component.facialRecognitionFail = true;
                        component.isLoading = false;
                        clearInterval(component.faceRecognitionInterval);
                    }
                }).catch(function () {
                    component.facialRecognitionFail = true;
                    component.isLoading = false;
                    clearInterval(component.faceRecognitionInterval);
                });
            },
            takePhoto() {
                this.$nextTick(() => {
                    let video = document.getElementById("cameraFrameHidden");
                    let canvas = document.createElement('canvas');
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    let ctx = canvas.getContext('2d');
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    this.img = canvas.toDataURL("image/jpeg");

                    this.handleRecognition();
                });
            },
            candidateRetryFacialRecognition() {
                this.facialRecognitionFail = false;
                this.showModalFaceRecognition = true;
            },
            callFacialRecognition() {
                this.faceRecognitionInterval = setInterval(() => {
                    this.startRecognition();
                }, 10000);
            },
            passFacialRecognition(){
                this.showModalFaceRecognition = false;
                this.callFacialRecognition();
            },
            startRecognition() {
                this.takePhoto();
            },
            handleRecognition(){
                if (this.enableTakePhoto) {
                  this.uploadPhoto();
                } else {
                  this.tryFacialRecognition();
                }
            },
            uploadPhoto() {
                let data = {};
                let baseUrl = getBaseSgpUrlNoSlug();
                let urlSavePhoto = baseUrl + this.urlSavePhoto;

                if (this.realTimePhotoValidation) {
                    data = {
                        is_first_recognition: false,
                        is_skipped: false,
                        image_base64: this.img,
                        unlockable_facial_recognition: true,
                        item_generated_id: this.itemGeneratedId,
                        bucket_name: this.bucketName,
                        url_save_photo: urlSavePhoto,
                        candidate_photo: this.candidatePhoto,
                    };
                } else {
                    data = {
                        image_base64: this.img,
                        item_generated_id: this.itemGeneratedId,
                        bucket_name: this.bucketName,
                        url_save_photo: urlSavePhoto
                    };
                }

                const headers = {"Content-Type": "application/json", "Authorization": 'Bearer ' + this.token};

                axios.post(this.uploadUrl, data, {headers: headers, withCredentials: false})
                    .then(response => {
                        if (this.failedFaceRecognitionNotification && !response.data.is_candidate_recognized) {
                            this.facialRecognitionFailNonBlocking = true;
                            this.isLoading = false;
                        }
                    })
            },
            leaveExam(){
                if (this.nativeProctoring) {
                    let data = {
                        "infraction_type": "CHANGE_TAB",
                        "item_generated": this.itemGeneratedId
                    };
                    registerInfraction(data);
                }
            },
            initVerifySharingScreen(){
                if(this.$store.state.onlineTest.configs.variables.native_proctoring){
                    this.statusScreenSubscription = screenSharingStatus$.get().subscribe(obj => {
                        if(obj && obj.state !== "streaming" && this.kinesisConnection.screen) this.kinesisConnection.screen.stop();
                    });
                }
            },
        },
        beforeUnmount() {
          this.isMounted = false;
          if(this.faceRecognitionInterval) clearInterval(this.faceRecognitionInterval);
          if (this.screenStreamSubscription) this.screenStreamSubscription.unsubscribe();
          if (this.retryKinesisInterval) clearTimeout(this.retryKinesisInterval);
          if (this.statusScreenSubscription) this.statusScreenSubscription.unsubscribe();
          if (this.kinesisConnection?.camera) this.kinesisConnection.camera.stop();
          if (this.kinesisConnection?.screen) this.kinesisConnection.screen.stop();
          if (window.stream){
              window.stream.getTracks().forEach(function(track) {
                  track.stop();
              });
          }

          if (this.refreshTimers.camera) clearTimeout(this.refreshTimers.camera);
          if (this.refreshTimers.screen) clearTimeout(this.refreshTimers.screen);
        },
        mounted() {
            this.itemGeneratedId = parseInt(this.$route.params.key) || this.$route.params.key;
            this.isMounted = true;
            this.loadCamera();
            this.initVerifySharingScreen();

            window.onblur = () => {
                this.leaveExam()
            };
        }
    }
</script>

<style scoped>

.non-blocking-notification {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: rgba(220, 53, 69, 0.95);
  color: #fff;
  padding: 15px 20px;
  border-radius: 8px;
  z-index: 9999;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  font-size: 14px;
  max-width: 350px;
  animation: slideInUp 0.4s ease forwards;
}

.close-button {
  top: 10px;
  right: 15px;
  cursor: pointer;
  font-weight: bold;
  font-size: 18px;
  color: #fff;
  background: none;
  border: none;
}

.non-blocking-notification p {
  margin: 0;
  padding-right: 25px;
  line-height: 1.5;
  text-align: justify;
}

@keyframes slideInUp {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
